import { useState } from "react";
import { ShieldCheck, X, ShieldAlert, Camera, MicIcon, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../api";

/**
 * GroundRulesModal — the one-time acceptance gate for the VI Sales
 * Certification. Renders as a full-screen overlay. Start buttons on the
 * Cert Hub stay disabled until the candidate ticks the checkbox and taps
 * "Enter certification". Persisted server-side on the assignment doc
 * (ground_rules_accepted_at) so it doesn't re-appear on refresh.
 *
 * Props:
 *   • open              — bool; whether to render the modal
 *   • onDismiss         — called when the user closes the modal WITHOUT
 *                          accepting (e.g. re-opened from the ribbon link)
 *   • onAccepted        — called with the new acceptance timestamp after a
 *                          successful POST to /ground-rules/accept
 *   • initialAccepted   — if the candidate has already accepted, we show
 *                          the modal in "read-only" mode with a Close btn
 *                          instead of an Accept flow.
 */
export default function GroundRulesModal({ open, onDismiss, onAccepted, initialAccepted }) {
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async () => {
    setSaving(true); setErr("");
    try {
      const r = await api.post("/participant/salescert/ground-rules/accept");
      onAccepted?.(r.data.ground_rules_accepted_at);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/80 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto"
      data-testid="ground-rules-modal"
    >
      <div className="max-w-2xl mx-auto bg-white border-2 border-[#0A0A0A] shadow-2xl">
        <div className="sticky top-0 bg-[#5F004B] text-white px-5 py-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFC600] font-bold">
              VI Sales Certification 2026 · Ground Rules
            </div>
            <h2 className="font-heading font-black text-xl sm:text-2xl mt-1 leading-tight">
              Please read before you begin
            </h2>
          </div>
          {initialAccepted && (
            <button
              onClick={onDismiss}
              className="text-white/70 hover:text-white p-1"
              aria-label="Close"
              data-testid="ground-rules-close"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-5 sm:p-7 space-y-6 text-[13px] sm:text-sm text-[#0A0A0A] leading-relaxed">
          {/* Mobile-specific quick-hits */}
          <div className="bg-[#FFF8E1] border-l-4 border-[#F59E0B] p-4 space-y-2" data-testid="ground-rules-mobile-hint">
            <div className="flex items-start gap-2">
              <MicIcon size={18} className="text-[#EE2737] flex-shrink-0 mt-0.5" />
              <div>
                <strong>On iPhone / Android:</strong> The AI customer speaks by voice. You&apos;ll tap once at the start to unlock audio, then it plays automatically. <strong>Turn your volume up now.</strong>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck size={18} className="text-[#EE2737] flex-shrink-0 mt-0.5" />
              <div>
                <strong>Voice matching:</strong> Speak in your natural voice, at normal volume, into the <em>same</em> mic (built-in or headset — pick one and stick with it).
              </div>
            </div>
          </div>

          {/* Green — environment */}
          <section data-testid="rules-environment">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-[#059669]" />
              <h3 className="font-heading font-bold text-base">Set your environment</h3>
            </div>
            <ul className="space-y-1.5 pl-5 list-disc marker:text-[#059669]">
              <li>Use your <strong>phone in portrait mode</strong> (or a laptop, if your network permits). Chrome, Safari or Edge — latest version.</li>
              <li>Sit in a well-lit, quiet room. Face the front camera directly.</li>
              <li><strong>Use the same mic the entire attempt</strong> — don&apos;t switch between speaker, headset, AirPods and earpiece mid-way. Mic-swaps will fail voice matching.</li>
              <li>Turn the volume up. On iPhone, set the physical silent switch to <strong>RING</strong> (not silent).</li>
              <li>Turn on Do Not Disturb / focus mode. If a WhatsApp call arrives and you leave the tab, the section may auto-forfeit.</li>
              <li>Wi-Fi ≥ 5 Mbps preferred. On mobile data, hold your phone still and stay in one signal spot.</li>
              <li>Keep your <strong>VI Employee ID card</strong> handy — you&apos;ll upload it once at the start.</li>
            </ul>
          </section>

          {/* Yellow — identity & voice */}
          <section data-testid="rules-identity">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
              <h3 className="font-heading font-bold text-base">Identity &amp; voice</h3>
            </div>
            <ul className="space-y-1.5 pl-5 list-disc marker:text-[#F59E0B]">
              <li><strong>Full ID + selfie verification runs once</strong> at the start of your first section.</li>
              <li>Before each subsequent section, a <strong>5-second face re-check</strong> runs (no ID upload again).</li>
              <li>Periodic camera checks compare your face to the enrolled selfie throughout — a stand-in for you <em>will</em> be flagged.</li>
              <li>Every role-play turn is compared to your enrolled voice. <strong>Only your own voice, on the same mic, will pass.</strong> Playback from another phone, someone else speaking, or a screen-recorded voice will be rejected mid-turn.</li>
              <li>Screenshots of any assessment screen are watermarked with <strong>your name and a timestamp</strong>. Any leaked image is traceable back to you.</li>
            </ul>
          </section>

          {/* Red — conduct */}
          <section data-testid="rules-conduct">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full bg-[#EE2737]" />
              <h3 className="font-heading font-bold text-base">Conduct</h3>
            </div>
            <ul className="space-y-1.5 pl-5 list-disc marker:text-[#EE2737]">
              <li><strong>Do NOT</strong> switch tabs or apps, take screenshots, or use screen-recording. All are logged and reduce your integrity score.</li>
              <li><strong>Do NOT</strong> refresh mid-attempt — MCQ forfeits that attempt (0 marks) and consumes one of your two slots. Sales Pitch and Role Play each allow two attempts (best kept) — a mid-attempt refresh forfeits the current attempt and consumes one slot.</li>
              <li><strong>Do NOT</strong> wear a mask, cap or dark glasses. Face-recognition needs a clear view.</li>
              <li><strong>Do NOT</strong> have anyone else in the frame or speaking in the background.</li>
              <li><strong>Do NOT</strong> share questions on WhatsApp, Slack or with peers — every screen is watermarked to trace leaks.</li>
              <li><strong>Do NOT</strong> read a scripted answer verbatim in the Sales Pitch or Role Play — the AI flags monotone reading and penalises it.</li>
            </ul>
          </section>

          {/* Sections at a glance */}
          <section data-testid="rules-sections">
            <div className="flex items-center gap-2 mb-2">
              <Camera size={16} className="text-[#5F004B]" />
              <h3 className="font-heading font-bold text-base">Sections at a glance</h3>
            </div>
            <div className="border border-[#E5E7EB] text-xs">
              <div className="grid grid-cols-4 bg-[#F5F3EE] px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#525252]">
                <div>Section</div><div>Time</div><div>Attempts</div><div className="col-span-1">Tests</div>
              </div>
              <div className="grid grid-cols-4 px-3 py-2 border-t border-[#E5E7EB]">
                <div className="font-semibold">Knowledge MCQ</div><div>20 min · 10 Qs</div><div>2 (best kept)</div><div>Ascend Enterprise product knowledge</div>
              </div>
              <div className="grid grid-cols-4 px-3 py-2 border-t border-[#E5E7EB]">
                <div className="font-semibold">Sales Pitch</div><div>4 minutes · 1 recording</div><div>2 (best kept)</div><div>Structured pitch delivery</div>
              </div>
              <div className="grid grid-cols-4 px-3 py-2 border-t border-[#E5E7EB]">
                <div className="font-semibold">Role Play</div><div>5 min · voice-only</div><div>2 (best kept)</div><div>Consultative selling under objection</div>
              </div>
            </div>
            <div className="text-[11px] text-[#525252] mt-2">
              Your certificate will be shared once VI L&amp;D releases the results.
            </div>
          </section>

          {/* Consequence banner */}
          <div className="bg-[#FEE2E2] border-l-4 border-[#DC2626] p-4 text-[13px] text-[#7F1D1D]" data-testid="rules-consequence">
            <div className="flex items-start gap-2">
              <ShieldAlert size={18} className="flex-shrink-0 mt-0.5" />
              <div>
                Breaches of these rules can lead to <strong>attempt disqualification, certification revocation, and escalation to the reporting manager</strong>. Please take this seriously.
              </div>
            </div>
          </div>

          {/* Acceptance */}
          {!initialAccepted && (
            <div className="border-t border-[#E5E7EB] pt-5 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="ground-rules-checkbox-label">
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={(e) => setTicked(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#EE2737]"
                  data-testid="ground-rules-checkbox"
                />
                <span className="text-sm">
                  I have read and will follow the ground rules above. I confirm that <strong>I am the enrolled candidate</strong> and will complete every section myself.
                </span>
              </label>
              {err && (
                <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-xs px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              <button
                onClick={submit}
                disabled={!ticked || saving}
                className="btn btn-primary w-full justify-center !py-3 !text-base disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ground-rules-accept"
              >
                <ShieldCheck size={16} /> {saving ? "Locking in…" : "Enter certification"}
              </button>
            </div>
          )}
          {initialAccepted && (
            <div className="border-t border-[#E5E7EB] pt-5">
              <div className="bg-[#DCFCE7] border border-[#86EFAC] text-[#166534] text-xs px-3 py-2 mb-3" data-testid="ground-rules-already-accepted">
                You accepted these rules on {new Date(initialAccepted).toLocaleString()}.
              </div>
              <button
                onClick={onDismiss}
                className="btn btn-secondary w-full justify-center"
                data-testid="ground-rules-close-secondary"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
